// Custom packages
import {
  archiveAttachedFiles,
  canActOnArchiveContract,
  canDeleteArchiveDocument,
  canEditArchiveDocumentData,
  canExpireArchiveContract,
  canPrintArchiveModule,
} from "src/app/modules/properties/document-archive-row-actions.utils";
import { canPrintArchiveRowQr } from "src/app/modules/properties/document-archive-qr-print.utils";
import {
  archiveSearchDay,
  escapeArchiveSearchText,
} from "src/app/modules/properties/document-archive-search.utils";
import {
  ARCHIVE_DEADLINES_MAX_LIMIT,
  DocumentArchiveDeadlineFilters,
  DocumentArchiveDeadlineSortKey,
  DocumentArchiveDeadlinesParams,
} from "src/app/shared/interfaces/document-archive-deadline.interface";
import { DocumentArchiveDocListItem } from "src/app/shared/interfaces/document-archive-doc.interface";
import { DocumentArchiveSearchRow } from "src/app/shared/interfaces/document-archive-search.interface";

/**
 * Rules of «Documenti in scadenza» on the 3.0.0 archive (NEW-M15), on
 * `GET /document-archive-docs/deadlines`: the successors of the legacy page «Scadenze documenti»
 * (`deadlines/list/documents`) and of the legacy page of one property «Elenco documenti con
 * scadenza» (`properties/:propertyId/documentsWithExpiration`).
 *
 * Pure functions, free of Angular: every decision of the two pages can be tested on its own.
 *
 * @since 3.0.0
 */

/** The roles that read the deadlines: the back-end answers 403 to any other (guests, taggers) */
export const DEADLINES_V3_READER_ROLE_TYPES: string[] = ["admin", "operator", "master", "collaborator"];

/** Rows per page when the page opens and after «Resetta filtri», as in the legacy page */
export const DEADLINES_V3_PAGE_SIZE = 10;

/** The page sizes the paginator offers: those of the legacy page */
export const DEADLINES_V3_PAGE_SIZE_OPTIONS: number[] = [10, 15, 20, 50, 75, 100];

/** The list opens sorted by expiry date, going up, as the legacy page */
export const DEADLINES_V3_DEFAULT_SORT: DocumentArchiveDeadlineSortKey = "endDate";

/** @see DEADLINES_V3_DEFAULT_SORT */
export const DEADLINES_V3_DEFAULT_DIRECTION = "asc";

/** Name the page keeps its filters under in ListsService: never the legacy page's `deadlines` */
export const DEADLINES_V3_LIST_SETTING = "deadlinesV3";

/**
 * Name the page of one property keeps its filters under, with its property: never the one of the
 * general page, whose filters are others, nor the legacy `deadlines`, which both legacy pages share
 */
export const DEADLINES_V3_PROPERTY_LIST_SETTING = "deadlinesV3Property";

/** The columns of the general page: those of the legacy page «Scadenze documenti», in its order */
export const DEADLINES_V3_GENERAL_COLUMNS: string[] = [
  "index",
  "property",
  "masterCode",
  "structureObjectName",
  "document",
  "providerName",
  "memoTime",
  "renewalType",
  "endDate",
  "actions",
];

/**
 * The columns of the page of one property: those the legacy page «Elenco documenti con scadenza»
 * shows (its `deadlinesDisplayedColumns`), in its order. No property, no master code and no supplier,
 * as there.
 */
export const DEADLINES_V3_PROPERTY_COLUMNS: string[] = [
  "index",
  "structureObjectName",
  "document",
  "memoTime",
  "renewalType",
  "endDate",
  "actions",
];

/** At most this many properties are offered by the «Immobile» filter */
export const DEADLINES_V3_TYPEAHEAD_LIMIT = 20;

/** The «Immobile» filter searches from this many characters on */
export const DEADLINES_V3_MIN_TERM_LENGTH = 2;

/** `data.tipoDiRinnovo` of a document that renews by itself, shown «Tacito» */
export const DEADLINES_V3_TACIT_RENEWAL = "Rinnovo automatico";

/** The two permissions of «Esporta»: the legacy one, and the one the back-end enforces */
const EXPORT_PERMISSIONS = ["canExportDeadlines", "canExportSubscriptions"];

/** The permission the legacy page of one property asks before it shows its list */
const PROPERTY_PAGE_PERMISSION = "canReadPropertiesExpirationsList";

/** The platform staff: the legacy page of one property shows them the list of any property */
const PLATFORM_ROLE_TYPES = ["admin", "operator"];

/** The members of a company: the legacy page of one property asks them the document upload too */
const MEMBER_ROLE_TYPES = ["master", "collaborator"];

/** Up to two years a notice period is shown as stored, in days; beyond, in years: the legacy rule */
const NOTICE_DAYS_AS_STORED = 730;

/** An id as the back-end writes it */
const OBJECT_ID_PATTERN = /^[0-9a-fA-F]{24}$/;

/** Every value `sort` accepts (@see DocumentArchiveDeadlineSortKey) */
const SORT_KEYS: DocumentArchiveDeadlineSortKey[] = [
  "property",
  "area",
  "document",
  "endDate",
  "memoTime",
  "renewalType",
  "providerName",
  "structureObjectName",
  "createdAt",
  "masterCode",
];

/** The filters typed as free text: sent trimmed, and only when something is left */
type DeadlinesV3TextFilter =
  | "masterCode"
  | "structureObjectName"
  | "providerName"
  | "document"
  | "memoTime"
  | "general";

/** The text filters of the general page: one under each of its text columns, and no free search */
const GENERAL_TEXT_FILTERS: DeadlinesV3TextFilter[] = [
  "masterCode",
  "structureObjectName",
  "providerName",
  "document",
  "memoTime",
];

/**
 * The text filters of the page of one property: one under each of its text columns, and the free
 * search of the legacy page, above the table
 */
const PROPERTY_TEXT_FILTERS: DeadlinesV3TextFilter[] = ["structureObjectName", "document", "memoTime", "general"];

/**
 * The page a table is on: the general page, or the page of one property, which lists that property
 * only (`search[property]`, always sent) and adds the free search of its legacy page.
 *
 * @since 3.0.0
 */
export type DeadlinesV3Scope = { kind: "general" } | { kind: "property"; propertyId: string };

/** @see DeadlinesV3Scope */
export const DEADLINES_V3_GENERAL_SCOPE: DeadlinesV3Scope = { kind: "general" };

/**
 * A property offered by the «Immobile» filter.
 *
 * @since 3.0.0
 */
export interface DeadlinesV3PropertyOption {
  _id: string;
  name: string;
}

/**
 * What the filters of the table hold, as the page reads them.
 *
 * @since 3.0.0
 */
export interface DeadlinesV3FilterValues {
  /** The property picked in «Immobile», or the text being typed in it, which filters nothing */
  property: unknown;
  /** The first expiry day: the value of the datepicker */
  endDate: unknown;
  masterCode: unknown;
  structureObjectName: unknown;
  providerName: unknown;
  document: unknown;
  memoTime: unknown;
  renewalType: unknown;
  /** The free search of the page of one property; the general page has none */
  general?: unknown;
}

/**
 * The window and the order of the table.
 *
 * @since 3.0.0
 */
export interface DeadlinesV3PageState {
  pageIndex: number;
  pageSize: number;
  sortActive: string;
  sortDirection: string;
}

/**
 * «Preavviso» as the legacy page shows it: the days as stored, or the years beyond two years.
 *
 * @since 3.0.0
 */
export interface DeadlinesV3Notice {
  text: string;
  /** True when `text` counts years: the page prints «anni» after it */
  inYears: boolean;
}

/**
 * A row of the table, with what each column prints.
 *
 * @since 3.0.0
 */
export interface DeadlinesV3Row {
  /** The row as the back-end served it: the download and the menu read it */
  row: DocumentArchiveSearchRow;
  propertyId: string;
  propertyName: string;
  masterCode: string;
  /** CURRENT name of the element the row hangs from */
  structureObjectName: string;
  /** The label the back-end filters and sorts «Documento» by: @see deadlinesV3DocumentLabel */
  documentLabel: string;
  supplierName: string;
  notice: DeadlinesV3Notice | null;
  /** «Tacito»: the document renews by itself */
  tacit: boolean;
  /** Expiry, ISO 8601, for the date pipe of the table */
  dateExpired: string | null;
  /** Written in the colour of an obligation not yet expired, as in the legacy page */
  mandatoryColour: boolean;
}

/**
 * The entries of the menu of a row: the eight of the legacy deadlines pages (FE, 21 October 2026,
 * answer A), with the rules of the 3.0.0 dossier.
 *
 * @since 3.0.0
 */
export interface DeadlinesV3RowMenu {
  show: boolean;
  edit: boolean;
  download: boolean;
  remove: boolean;
  /** «Qr-code» is in the menu: enabled only when `qrEnabled` says so, shown disabled otherwise */
  qr: boolean;
  /**
   * «Qr-code» prints (FE-M10): once the 3.0.0 archive is live, on a row the public page shows
   * (@see canPrintArchiveRowQr). Before the switch the entry stays disabled: the rows get new ids
   * before it (FLAT-A2), and a sticker printed on an id of today would answer «not found» for good.
   */
  qrEnabled: boolean;
  print: boolean;
  cancel: boolean;
  expire: boolean;
}

/** The entries of the menu that need the tree of the property of the row */
export type DeadlinesV3TreeAction = "show" | "edit" | "remove" | "qr" | "print" | "cancel" | "expire";

/** What a failed load of the list asks of the user */
export type DeadlinesV3ErrorKind = "invalidParameters" | "forbidden" | "network" | "other";

/** What a failed Excel asks of the user */
export type DeadlinesV3ExportErrorKind = "rateLimited" | "forbidden" | "network" | "other";

/**
 * Whether the role reads the deadlines: anything else is refused by the back-end, so the page shows
 * «not authorized» without asking.
 *
 * @since 3.0.0
 *
 * @param roleType Type of the active role
 */
export function isDeadlinesV3Reader(roleType: string): boolean {
  return typeof roleType === "string" && DEADLINES_V3_READER_ROLE_TYPES.indexOf(roleType) > -1;
}

/**
 * Whether «Esporta» is offered: with the permission the legacy page asks (`canExportDeadlines`) AND
 * the one the back-end enforces (`canExportSubscriptions`), as the 3.0.0 pages do for every action.
 *
 * @since 3.0.0
 *
 * @param permissions Permissions of the active role
 */
export function canExportDeadlinesV3(permissions: string[]): boolean {
  const granted = Array.isArray(permissions) ? permissions : [];
  return EXPORT_PERMISSIONS.every((permission) => granted.indexOf(permission) > -1);
}

/**
 * Whether the page of one property shows its list to the active role: the rule of the legacy page
 * «Elenco documenti con scadenza», and of its button in the home of the property, kept as it is.
 * `canReadPropertiesExpirationsList`, and then the platform staff on any property, a member of a
 * company only on a property with the document upload (@see hasDeadlinesV3DocumentUpload).
 *
 * The back-end lists no row of a property without the document upload anyway, whoever asks.
 *
 * @since 3.0.0
 *
 * @param roleType       Type of the active role
 * @param permissions    Permissions of the active role
 * @param documentUpload Whether the property has the document upload
 */
export function canSeePropertyDeadlinesV3(
  roleType: string,
  permissions: string[],
  documentUpload: boolean
): boolean {
  const granted = Array.isArray(permissions) ? permissions : [];
  if (granted.indexOf(PROPERTY_PAGE_PERMISSION) === -1) {
    return false;
  }
  if (PLATFORM_ROLE_TYPES.indexOf(roleType) > -1) {
    return true;
  }
  return MEMBER_ROLE_TYPES.indexOf(roleType) > -1 && documentUpload === true;
}

/**
 * Whether a property, as `GET properties/:id` answers it, has the document upload, read as the legacy
 * page of one property reads it: a plan (`subscription.key`) with `documentUpload` on.
 *
 * @since 3.0.0
 *
 * @param property The `data` of the answer
 */
export function hasDeadlinesV3DocumentUpload(property: unknown): boolean {
  const subscription =
    property && typeof property === "object" ? (property as { subscription?: unknown }).subscription : null;
  if (!subscription || typeof subscription !== "object") {
    return false;
  }
  const plan = subscription as { key?: unknown; documentUpload?: unknown };
  return !!plan.key && !!plan.documentUpload;
}

/**
 * The page a table is on, out of the property it is given: none for the general page, an id for the
 * page of that property.
 *
 * `null` for a property that is given and is no id, `""` included: such a page must ask nothing, as a
 * list asked without its property would hold the deadlines of every property.
 *
 * @since 3.0.0
 *
 * @param propertyId The property given to the table, `undefined` on the general page
 */
export function deadlinesV3Scope(propertyId: unknown): DeadlinesV3Scope | null {
  if (propertyId === undefined || propertyId === null) {
    return DEADLINES_V3_GENERAL_SCOPE;
  }
  return typeof propertyId === "string" && OBJECT_ID_PATTERN.test(propertyId)
    ? { kind: "property", propertyId }
    : null;
}

/**
 * The columns of the table on a page: @see DEADLINES_V3_GENERAL_COLUMNS, DEADLINES_V3_PROPERTY_COLUMNS
 *
 * @since 3.0.0
 *
 * @param kind The page
 */
export function deadlinesV3Columns(kind: DeadlinesV3Scope["kind"]): string[] {
  return (kind === "property" ? DEADLINES_V3_PROPERTY_COLUMNS : DEADLINES_V3_GENERAL_COLUMNS).slice();
}

/**
 * The name a page keeps its filters under in ListsService.
 *
 * @since 3.0.0
 *
 * @param scope The page
 */
export function deadlinesV3ListSettingName(scope: DeadlinesV3Scope): string {
  return scope && scope.kind === "property" ? DEADLINES_V3_PROPERTY_LIST_SETTING : DEADLINES_V3_LIST_SETTING;
}

/**
 * Whether what a page kept may be read back: always on the general page; on the page of one property
 * only when it was kept on the same property, so that the filters typed on one never filter another.
 *
 * @since 3.0.0
 *
 * @param saved What the page kept (`search` of its setting)
 * @param scope The page
 */
export function isDeadlinesV3SettingOf(saved: unknown, scope: DeadlinesV3Scope): boolean {
  if (!scope || scope.kind !== "property") {
    return true;
  }
  return !!saved && typeof saved === "object" && (saved as { propertyId?: unknown }).propertyId === scope.propertyId;
}

/**
 * The property a value of «Immobile» stands for: an option picked in the list, or a setting saved
 * from one. Text being typed stands for none.
 *
 * @since 3.0.0
 *
 * @param value The value of the field
 */
export function deadlinesV3PropertyOption(value: unknown): DeadlinesV3PropertyOption | null {
  if (!value || typeof value !== "object") {
    return null;
  }
  const id = (value as { _id?: unknown })._id;
  if (typeof id !== "string" || !OBJECT_ID_PATTERN.test(id)) {
    return null;
  }
  const name = (value as { name?: unknown }).name;
  return { _id: id, name: typeof name === "string" ? name : "" };
}

/**
 * The filters the back-end receives, out of the values of the table. What is empty is left out.
 *
 * The expiry day is the calendar day picked (@see archiveSearchDay), a Rome day for the back-end.
 * The texts travel trimmed: the back-end measures them trimmed too.
 *
 * Each page sends the filters of its own columns only. The page of one property always sends its
 * property, whatever the values hold, and its free search; the general page sends the property
 * picked in «Immobile», and never a free search, which it does not have.
 *
 * @since 3.0.0
 *
 * @param values  The values of the filters
 * @param [scope] The page (@see deadlinesV3Scope); the general one when omitted
 */
export function deadlinesV3Filters(
  values: DeadlinesV3FilterValues,
  scope: DeadlinesV3Scope = DEADLINES_V3_GENERAL_SCOPE
): DocumentArchiveDeadlineFilters {
  const filters: DocumentArchiveDeadlineFilters = {};
  let propertyPage = false;
  if (scope && scope.kind === "property") {
    propertyPage = true;
    // Even with no other filter: without it the list would hold every property's deadlines
    filters.property = scope.propertyId;
  }
  if (!values) {
    return filters;
  }

  if (!propertyPage) {
    const property = deadlinesV3PropertyOption(values.property);
    if (property) {
      filters.property = property._id;
    }
  }

  const day = archiveSearchDay(values.endDate);
  if (day) {
    filters.endDate = day;
  }

  (propertyPage ? PROPERTY_TEXT_FILTERS : GENERAL_TEXT_FILTERS).forEach((name) => {
    const raw = values[name];
    const text = typeof raw === "string" ? raw.trim() : "";
    if (text) {
      filters[name] = text;
    }
  });

  if (values.renewalType === "tacit" || values.renewalType === "false") {
    filters.renewalType = values.renewalType;
  }

  return filters;
}

/**
 * The query of one page of the list.
 *
 * A size outside the bounds of the back-end falls back on the default rather than being refused, and
 * so does a column the back-end cannot sort by.
 *
 * @since 3.0.0
 *
 * @param page    The window and the order of the table
 * @param filters The filters (@see deadlinesV3Filters)
 */
export function deadlinesV3Params(
  page: DeadlinesV3PageState,
  filters: DocumentArchiveDeadlineFilters
): DocumentArchiveDeadlinesParams {
  const size =
    page && Number.isInteger(page.pageSize) && page.pageSize >= 1 && page.pageSize <= ARCHIVE_DEADLINES_MAX_LIMIT
      ? page.pageSize
      : DEADLINES_V3_PAGE_SIZE;
  const index = page && Number.isInteger(page.pageIndex) && page.pageIndex > 0 ? page.pageIndex : 0;
  const sort =
    page && SORT_KEYS.indexOf(page.sortActive as DocumentArchiveDeadlineSortKey) > -1
      ? (page.sortActive as DocumentArchiveDeadlineSortKey)
      : DEADLINES_V3_DEFAULT_SORT;

  return {
    start: index * size,
    limit: size,
    sort,
    sortVersus: page && page.sortDirection === "desc" ? "desc" : "asc",
    search: filters ? filters : {},
  };
}

/**
 * «Preavviso» as the legacy page shows `data.tempoPreavvisoDisdetta`: the stored text up to 730 days,
 * the days divided by 365 beyond, and nothing for a text that is no number. The same rule as the
 * Excel of the back-end.
 *
 * @since 3.0.0
 *
 * @param value `data.tempoPreavvisoDisdetta`
 */
export function deadlinesV3Notice(value: unknown): DeadlinesV3Notice | null {
  const stored = typeof value === "number" ? String(value) : typeof value === "string" ? value : "";
  if (!stored.trim()) {
    return null;
  }
  const days = Number(stored);
  if (days <= NOTICE_DAYS_AS_STORED) {
    return { text: stored, inYears: false };
  }
  if (days > NOTICE_DAYS_AS_STORED) {
    return { text: String(days / 365), inYears: true };
  }
  return null;
}

/**
 * The label «Documento» prints: the ITALIAN translation of the document type, its title when it has
 * none, whatever the language of the page. It is the label the back-end filters and sorts the column
 * by, and the legacy page printed the Italian name too: printed in another language, what the user
 * reads would not be what the filter matches.
 *
 * @since 3.0.0
 *
 * @param row The row
 */
export function deadlinesV3DocumentLabel(row: DocumentArchiveSearchRow | null | undefined): string {
  const type = row && row.documentType ? row.documentType : null;
  if (!type) {
    return "";
  }
  if (type.translation && typeof type.translation.it === "string" && type.translation.it) {
    return type.translation.it;
  }
  return typeof type.title === "string" ? type.title : "";
}

/**
 * The rows of the table, from the items of the answer, in the order they came in: the back-end
 * sorted them.
 *
 * @since 3.0.0
 *
 * @param items The items of the answer
 */
export function deadlinesV3Rows(items: unknown): DeadlinesV3Row[] {
  const rows: DeadlinesV3Row[] = [];
  (Array.isArray(items) ? items : []).forEach((item: unknown) => {
    if (!item || typeof item !== "object" || typeof (item as { _id?: unknown })._id !== "string") {
      return;
    }
    const row = item as DocumentArchiveSearchRow;
    const data: { [key: string]: unknown } = row.data && typeof row.data === "object" ? row.data : {};
    const property = row.property && typeof row.property === "object" ? row.property : null;
    const propertyId =
      typeof row.propertyId === "string" && row.propertyId
        ? row.propertyId
        : property && typeof property._id === "string"
        ? property._id
        : "";

    rows.push({
      row,
      propertyId,
      propertyName: property && typeof property.name === "string" ? property.name : "",
      masterCode: property && typeof property.masterCode === "string" ? property.masterCode : "",
      structureObjectName: typeof row.structureObjectName === "string" ? row.structureObjectName : "",
      documentLabel: deadlinesV3DocumentLabel(row),
      supplierName: typeof row.supplierName === "string" ? row.supplierName : "",
      notice: deadlinesV3Notice(data.tempoPreavvisoDisdetta),
      tacit: data.tipoDiRinnovo === DEADLINES_V3_TACIT_RENEWAL,
      dateExpired: typeof row.dateExpired === "string" && row.dateExpired ? row.dateExpired : null,
      mandatoryColour: row.mandatory === true && row.expired !== true,
    });
  });
  return rows;
}

/**
 * The menu of a row for the active role (FE, 21 October 2026, answer A): the eight entries of the
 * legacy deadlines pages, each with the rule of the 3.0.0 dossier, so that a role keeps here what it
 * has there:
 *  - edit and delete ask for the legacy permission AND the one the back-end enforces
 *    (@see canEditArchiveDocumentData, canDeleteArchiveDocument);
 *  - the cancellation and the switch-off of a contract the rules of the dossier, the switch-off not
 *    for `master` (@see canActOnArchiveContract, canExpireArchiveContract);
 *  - «Qr-code»: before the switch on what is not a contract, as in the legacy page, shown disabled;
 *    after it, enabled on the rows the public page shows, contracts included — a contract is printed
 *    from its document, the contracts table has no QR of its own (NEW-A3);
 *  - «Stampa modulo doc» with the rule of the dossier (@see canPrintArchiveModule).
 *
 * Every entry needs the document FILED and not deleted, as in the dossier. The list only serves such
 * rows, and the check is repeated on the fresher row of the tree before a dialog opens.
 *
 * @since 3.0.0
 *
 * @param row         The row: of the list, or the same row read in the tree of its property, which
 *                    carries the same state without the labels of the list
 * @param roleType    Type of the active role
 * @param permissions Permissions of the active role
 * @param qrLive      Whether «Qr-code» prints in this build (@see isArchiveV3Live)
 */
export function deadlinesV3RowMenu(
  row: DocumentArchiveDocListItem | null | undefined,
  roleType: string,
  permissions: string[],
  qrLive = false
): DeadlinesV3RowMenu {
  const none: DeadlinesV3RowMenu = {
    show: false,
    edit: false,
    download: false,
    remove: false,
    qr: false,
    qrEnabled: false,
    print: false,
    cancel: false,
    expire: false,
  };
  if (!row || row.present !== true || row.deleted === true) {
    return none;
  }

  const documentActions = isDeadlinesV3Reader(roleType);
  const granted = Array.isArray(permissions) ? permissions : [];
  // No block here: the list does not carry it. The table checks again on the row of the tree, with
  // its block, before the label opens.
  const qrEnabled = canPrintArchiveRowQr(row, null, documentActions, qrLive === true);

  return {
    show: documentActions,
    edit: canEditArchiveDocumentData(documentActions, granted),
    download: archiveAttachedFiles(row).length > 0,
    remove: canDeleteArchiveDocument(documentActions, granted),
    qr: qrLive ? qrEnabled : documentActions && row.contract !== true,
    qrEnabled,
    print: canPrintArchiveModule(row, documentActions),
    cancel: documentActions && canActOnArchiveContract(row, roleType, granted),
    expire: documentActions && canExpireArchiveContract(row, roleType, granted),
  };
}

/**
 * Whether the menu of a row has an entry that does something: without one the button is not drawn.
 * The disabled «Qr-code» alone is no reason to draw it; the enabled one is.
 *
 * @since 3.0.0
 *
 * @param menu The entries
 */
export function hasDeadlinesV3RowMenu(menu: DeadlinesV3RowMenu | null | undefined): boolean {
  return (
    !!menu &&
    (menu.show ||
      menu.edit ||
      menu.download ||
      menu.remove ||
      menu.qrEnabled ||
      menu.print ||
      menu.cancel ||
      menu.expire)
  );
}

/**
 * Whether an entry that needs the tree is offered by a menu.
 *
 * @since 3.0.0
 *
 * @param menu   The entries
 * @param action The entry
 */
export function isDeadlinesV3ActionAllowed(
  menu: DeadlinesV3RowMenu | null | undefined,
  action: DeadlinesV3TreeAction
): boolean {
  if (!menu) {
    return false;
  }
  switch (action) {
    case "show":
      return menu.show;
    case "edit":
      return menu.edit;
    case "remove":
      return menu.remove;
    case "qr":
      return menu.qrEnabled;
    case "print":
      return menu.print;
    case "cancel":
      return menu.cancel;
    case "expire":
      return menu.expire;
    default:
      return false;
  }
}

/**
 * What a failed load of the list asks of the user.
 *
 * `network` is also what a plain `Error` means: ApiService emits a failure with no status (network
 * down, request blocked) as an Error value, which the service throws back as an error.
 *
 * @since 3.0.0
 *
 * @param err The error of the load
 */
export function deadlinesV3ErrorKind(err: unknown): DeadlinesV3ErrorKind {
  if (err instanceof Error) {
    return "network";
  }
  if (!err || typeof err !== "object") {
    return "other";
  }
  switch ((err as { status?: unknown }).status) {
    case 0:
      return "network";
    case 400: {
      // The 400 of the route answers the JSON envelope of the back-end; anything else, such as the
      // HTML page of a proxy, is not one
      const body = (err as { error?: unknown }).error;
      return body && typeof body === "object" ? "invalidParameters" : "other";
    }
    case 403:
      return "forbidden";
    default:
      return "other";
  }
}

/**
 * What a failed Excel asks of the user.
 *
 * @since 3.0.0
 *
 * @param err The error of the download, or the Error value ApiService emits without a status
 */
export function deadlinesV3ExportErrorKind(err: unknown): DeadlinesV3ExportErrorKind {
  if (err instanceof Error) {
    return "network";
  }
  if (!err || typeof err !== "object") {
    return "other";
  }
  switch ((err as { status?: unknown }).status) {
    case 0:
      return "network";
    case 403:
      return "forbidden";
    case 429:
      return "rateLimited";
    default:
      return "other";
  }
}

/**
 * The message of an error envelope of the back-end, `""` when there is none: the body of a JSON
 * error, or its text when it came as a Blob and was read as text.
 *
 * @since 3.0.0
 *
 * @param body The body, as an object or as the text of a Blob
 */
export function deadlinesV3BackEndMessage(body: unknown): string {
  let envelope: unknown = body;
  if (typeof body === "string") {
    try {
      envelope = JSON.parse(body);
    } catch (e) {
      return "";
    }
  }
  if (!envelope || typeof envelope !== "object") {
    return "";
  }
  const message = (envelope as { message?: unknown }).message;
  return typeof message === "string" ? message.trim() : "";
}

/**
 * The text a typed property search is sent with, or null when it is too short to search.
 *
 * The text is ESCAPED: the back-end turns `search[general]` of `GET properties` into a regular
 * expression without escaping it, so a "(" would break the search and a crafted pattern could make it
 * backtrack. Escaped, what the user typed is matched literally.
 *
 * @since 3.0.0
 *
 * @param term The text typed in «Immobile»
 */
export function deadlinesV3TypeaheadTerm(term: unknown): string | null {
  const text = typeof term === "string" ? term.trim() : "";
  return text.length >= DEADLINES_V3_MIN_TERM_LENGTH ? escapeArchiveSearchText(text) : null;
}

/**
 * The properties of an answer of `GET properties` as options of «Immobile», in the order they came.
 * An entry without a valid id is left out: it could not be filtered by.
 *
 * @since 3.0.0
 *
 * @param res The answer (`data.items`)
 */
export function deadlinesV3PropertyOptions(res: unknown): DeadlinesV3PropertyOption[] {
  const data = res && typeof res === "object" ? (res as { data?: unknown }).data : null;
  const items = data && typeof data === "object" ? (data as { items?: unknown }).items : null;
  const options: DeadlinesV3PropertyOption[] = [];
  (Array.isArray(items) ? items : []).forEach((item: unknown) => {
    const option = deadlinesV3PropertyOption(item);
    if (option) {
      options.push(option);
    }
  });
  return options;
}
